import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface ChatBotContextType {
  isOpen: boolean;
  initialMessage: string | null;
  openChatBot: (message?: string) => void;
  closeChatBot: () => void;
  setInitialMessage: (message: string | null) => void;
}

const ChatBotContext = createContext<ChatBotContextType | undefined>(undefined);

export const useChatBot = () => {
  const context = useContext(ChatBotContext);
  if (!context) {
    throw new Error('useChatBot must be used within a ChatBotProvider');
  }
  return context;
};

interface ChatBotProviderProps {
  children: ReactNode;
}

export const ChatBotProvider: React.FC<ChatBotProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [initialMessage, setInitialMessage] = useState<string | null>(null);

  const openChatBot = useCallback((message?: string) => {
    if (message) {
      setInitialMessage(message);
    }
    setIsOpen(true);
    document.body.classList.add('chat-open');
  }, []);

  const closeChatBot = useCallback(() => {
    setIsOpen(false);
    document.body.classList.remove('chat-open');
    setInitialMessage(null);
  }, []);

  const handleSetInitialMessage = useCallback((message: string | null) => {
    setInitialMessage(message);
  }, []);

  return (
    <ChatBotContext.Provider
      value={{
        isOpen,
        initialMessage,
        openChatBot,
        closeChatBot,
        setInitialMessage: handleSetInitialMessage,
      }}
    >
      {children}
    </ChatBotContext.Provider>
  );
};
